import { useCallback, useState, useEffect, useRef } from 'react';
import { 
  EdgeProps, 
  getBezierPath, 
  BaseEdge,
  EdgeLabelRenderer,
  useReactFlow
} from 'reactflow';
import { Trash2 } from 'lucide-react';

export function DeletableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
}: EdgeProps) {
  const { setEdges } = useReactFlow();
  const [isHovered, setIsHovered] = useState(false);
  const [keepVisible, setKeepVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Mantener visible el botón por 5 segundos después del hover
  useEffect(() => {
    if (isHovered) {
      setKeepVisible(true);
      
      // Limpiar timeout anterior si existe
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Mantener visible por 5 segundos
      timeoutRef.current = setTimeout(() => {
        setKeepVisible(false);
      }, 5000);
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isHovered]);

  // Ocultar botón al hacer clic fuera del edge/botón (incluso fuera del canvas)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (keepVisible && buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setKeepVisible(false);
        setIsHovered(false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      }
    };

    if (keepVisible) {
      // Usar capture: true para capturar clics antes que otros handlers
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('click', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [keepVisible]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const onEdgeDelete = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  }, [id, setEdges]);

  const showButton = isHovered || keepVisible;

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      
      {/* Path invisible MUY GRUESO para capturar hover fácilmente */}
      <path
        d={edgePath}
        fill="none"
        strokeWidth={50}
        stroke="transparent"
        style={{ 
          cursor: 'pointer',
          pointerEvents: 'stroke'
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
      
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
        >
          <button
            ref={buttonRef}
            onClick={onEdgeDelete}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-all duration-300 shadow-xl border-2 border-white ${
              showButton ? 'opacity-100 scale-110' : 'opacity-0 scale-0'
            }`}
            style={{
              zIndex: 10000,
            }}
          >
            <Trash2 className="h-4 w-4 text-white" />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
